// 📄 ticketService.ts
// Tickets de soporte de PLATAFORMA (no puedo crear un proyecto, invitar a
// alguien...). Los del día a día del cliente van a la Mesa de ayuda del
// proyecto (mesaAyudaService.ts). Cualquier usuario logueado puede abrir
// uno desde "Ayuda" (navbar), sin necesidad de estar en un proyecto — a
// diferencia de Incidentes (projects/{id}/incidentes), que sí requiere uno.
// Sin asignación: el usuario no sabe (ni debe elegir) quién lo va a atender,
// solo lo ve cualquier Admin desde SoporteHome.tsx.
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { queueEmail } from './mailService.ts';
import { listPlatformAdmins } from './userService.ts';

export type TicketPrioridad = 'Baja' | 'Media' | 'Alta' | 'Crítica';
export type TicketCategoria = 'Proyecto' | 'Organización' | 'Plataforma' | 'Otro';
export type TicketEstado = 'Abierto' | 'En revisión' | 'Resuelto' | 'Cerrado';

export const TICKET_PRIORIDADES: TicketPrioridad[] = ['Baja', 'Media', 'Alta', 'Crítica'];
export const TICKET_ESTADOS: TicketEstado[] = ['Abierto', 'En revisión', 'Resuelto', 'Cerrado'];
export const TICKET_CATEGORIAS: { value: TicketCategoria; label: string }[] = [
  { value: 'Proyecto', label: 'Problema con un proyecto' },
  { value: 'Organización', label: 'Problema con mi organización' },
  { value: 'Plataforma', label: 'Error general de la plataforma' },
  { value: 'Otro', label: 'Otro' }
];

export interface TicketFormData {
  descripcion: string;
  prioridad: TicketPrioridad;
  categoria: TicketCategoria;
  // Solo aplica cuando categoria === 'Proyecto' — igual criterio que
  // checklistStepKey en incidentes (opcional, solo cuando corresponde).
  proyectoId?: string;
  proyectoNombre?: string;
}

export interface Ticket extends TicketFormData {
  id: string;
  creadoPorUid: string;
  creadoPorNombre: string;
  creadoPorEmail: string;
  estado: TicketEstado;
  fechaCreacion?: any;
  // Conversación (D5): quién escribió el último mensaje y cuándo.
  ultimoMensajeEn?: any;
  ultimoMensajePor?: 'usuario' | 'soporte';
}

export interface TicketMensaje {
  id: string;
  texto: string;
  autorUid: string;
  autorNombre: string;
  autorRol: 'usuario' | 'soporte';
  creadoEn?: any;
}

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

const ticketsRef = () => collection(db, "soporteTickets");
const ticketRef = (ticketId: string) => doc(db, "soporteTickets", ticketId);

/**
 * Notifica por correo a TODOS los admins de plataforma — a diferencia de
 * notifyNuevoIncidente (que notifica al PM/asignado de un proyecto puntual),
 * acá no hay a quién más avisar porque el ticket no está ligado a un equipo.
 * No corta la creación del ticket si el envío falla (mismo criterio).
 */
const notifyNuevoTicket = async (ticket: Ticket): Promise<void> => {
  const admins = await listPlatformAdmins();
  const emails = admins.map(a => a.email).filter(Boolean);
  if (emails.length === 0) return;

  await queueEmail({
    to: emails,
    subject: `[Ticket de Soporte] ${ticket.prioridad} — ${ticket.categoria}`,
    html: `
      <p>Se abrió un nuevo ticket de soporte.</p>
      <p><strong>Prioridad:</strong> ${ticket.prioridad} &mdash; <strong>Categoría:</strong> ${ticket.categoria}</p>
      ${ticket.proyectoNombre ? `<p><strong>Proyecto relacionado:</strong> ${escapeHtml(ticket.proyectoNombre)}</p>` : ''}
      <p><strong>Reportado por:</strong> ${escapeHtml(ticket.creadoPorNombre)} (${escapeHtml(ticket.creadoPorEmail)})</p>
      <p><strong>Descripción:</strong></p>
      <p style="white-space:pre-wrap">${escapeHtml(ticket.descripcion)}</p>
    `
  });
};

export const createTicket = async (
  uid: string,
  nombre: string,
  email: string,
  data: TicketFormData
): Promise<string> => {
  if (!data.descripcion.trim()) throw new Error("La descripción es obligatoria.");

  const ref = doc(ticketsRef());
  const payload: Record<string, any> = {
    creadoPorUid: uid,
    creadoPorNombre: nombre,
    creadoPorEmail: email,
    descripcion: data.descripcion.trim(),
    prioridad: data.prioridad,
    categoria: data.categoria,
    estado: 'Abierto' as TicketEstado,
    fechaCreacion: serverTimestamp()
  };
  if (data.categoria === 'Proyecto' && data.proyectoId) {
    payload.proyectoId = data.proyectoId;
    payload.proyectoNombre = data.proyectoNombre || '';
  }

  await setDoc(ref, payload);

  await notifyNuevoTicket({ id: ref.id, ...(payload as Omit<Ticket, 'id'>) })
    .catch(err => console.error('Error notificando ticket nuevo:', err));

  return ref.id;
};

export const getMisTickets = async (uid: string): Promise<Ticket[]> => {
  const q = query(ticketsRef(), where("creadoPorUid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<Ticket, 'id'>) }))
    .sort((a, b) => (b.fechaCreacion?.toMillis?.() ?? 0) - (a.fechaCreacion?.toMillis?.() ?? 0));
};

/**
 * Igual que getMisTickets pero en vivo (D5): en "Ayuda" el estado y el aviso
 * "Soporte respondió" se actualizan sin recargar (p. ej. al cerrar el ticket).
 */
export const escucharMisTickets = (uid: string, onCambio: (tickets: Ticket[]) => void, onError: (err: Error) => void) =>
  onSnapshot(
    query(ticketsRef(), where("creadoPorUid", "==", uid)),
    (snap) => onCambio(
      snap.docs
        .map(d => ({ id: d.id, ...(d.data() as Omit<Ticket, 'id'>) }))
        .sort((a, b) => (b.fechaCreacion?.toMillis?.() ?? 0) - (a.fechaCreacion?.toMillis?.() ?? 0))
    ),
    onError
  );

/** Todos los tickets de la plataforma — solo Admin (ver firestore.rules). */
export const getAllTicketsAdmin = async (): Promise<Ticket[]> => {
  const snap = await getDocs(ticketsRef());
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<Ticket, 'id'>) }))
    .sort((a, b) => (b.fechaCreacion?.toMillis?.() ?? 0) - (a.fechaCreacion?.toMillis?.() ?? 0));
};

export const updateTicketEstado = async (ticketId: string, estado: TicketEstado) => {
  await updateDoc(ticketRef(ticketId), { estado });
};

// ── Conversación del ticket (D5 de plan-diagnostico-documentos.md) ──────
// Se lee directo (rules: creador o Admin) y se escribe por onehubApi, que
// guarda el mensaje con Admin SDK y avisa por correo a la otra parte.

export const escucharMensajes = (ticketId: string, onCambio: (mensajes: TicketMensaje[]) => void, onError: (err: Error) => void) =>
  onSnapshot(
    query(collection(db, 'soporteTickets', ticketId, 'mensajes'), orderBy('creadoEn', 'asc')),
    (snap) => onCambio(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TicketMensaje, 'id'>) }))),
    onError
  );

export const enviarMensajeTicket = async (ticketId: string, texto: string): Promise<void> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/support-tickets/${encodeURIComponent(ticketId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: JSON.stringify({ text: texto })
  });
  if (!res.ok) {
    const { error } = await res.json().catch(() => ({}));
    throw new Error(error || `Error ${res.status} al enviar el mensaje.`);
  }
};
